import React, { useState, useRef, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '../../context/AuthContext'
import { useTheme } from '../../context/ThemeContext'
import { I, Icon } from '../shared/ns-icons'
import NotificationDropdown from './NotificationDropdown'
import { MOD_KEY } from '../../utils/platform'

// ── Icon constants ────────────────────────────────────────────────────────────

const SEARCH_ICON = (
  <svg viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="11" cy="11" r="7"/>
    <path d="m21 21-4.35-4.35"/>
  </svg>
)

const LOGOUT_ICON = (
  <svg viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/>
    <polyline points="16 17 21 12 16 7"/>
    <line x1="21" y1="12" x2="9" y2="12"/>
  </svg>
)

const MENU_ICON = (
  <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="3" y1="6" x2="21" y2="6"/>
    <line x1="3" y1="12" x2="21" y2="12"/>
    <line x1="3" y1="18" x2="21" y2="18"/>
  </svg>
)

// ── Component ─────────────────────────────────────────────────────────────────

export default function Header({ onMenuClick }) {
  const { user, logout } = useAuth()
  const { isDark, toggleTheme } = useTheme()
  const navigate = useNavigate()

  const [menuOpen, setMenuOpen] = useState(false)
  const userRef = useRef(null)

  const isAdmin = user?.role === 'admin'
  const initials = user?.name
    ? user.name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2)
    : 'U'

  // Close the user menu on outside click
  useEffect(() => {
    const onDoc = (e) => {
      if (userRef.current && !userRef.current.contains(e.target)) setMenuOpen(false)
    }
    document.addEventListener('mousedown', onDoc)
    return () => document.removeEventListener('mousedown', onDoc)
  }, [])

  const goSettings = () => { setMenuOpen(false); navigate(isAdmin ? '/admin/settings' : '/client/settings') }
  const signOut = () => { setMenuOpen(false); logout() }

  return (
    <div className="topbar">

      {/* Hamburger — opens the sidebar drawer (mobile only, hidden on desktop) */}
      <button
        type="button"
        className="btn btn-icon btn-ghost topbar-mobile-menu"
        onClick={onMenuClick}
        aria-label="Open menu"
      >
        {MENU_ICON}
      </button>

      {/* Search — opens the command palette */}
      <button
        type="button"
        className="topbar-search"
        onClick={() => window.dispatchEvent(new CustomEvent('open-command-palette'))}
        aria-label="Open search"
      >
        {SEARCH_ICON}
        <span style={{ flex: 1, fontSize: 13, color: 'var(--text-faint)', textAlign: 'left' }}>Search…</span>
        <span style={{ display: 'flex', gap: 2 }}>
          <span className="ds-kbd">{MOD_KEY}</span>
          <span className="ds-kbd">K</span>
        </span>
      </button>

      <div className="topbar-right">

        {/* Theme toggle */}
        <button
          className="btn btn-icon btn-ghost"
          onClick={toggleTheme}
          title={isDark ? 'Switch to light mode' : 'Switch to dark mode'}
        >
          {isDark ? <I.sun /> : <I.moon />}
        </button>

        {/* Notifications */}
        <NotificationDropdown />

        <div className="vdiv" style={{ height: 18, margin: '0 4px' }} />

        {/* User + dropdown */}
        <div style={{ position: 'relative' }} ref={userRef}>
          <button className="topbar-user" onClick={() => setMenuOpen(o => !o)}>
            <div className="ds-avatar ds-avatar-1" style={{ width: 30, height: 30, fontSize: 11 }}>
              {initials}
            </div>
            <div style={{ textAlign: 'left' }}>
              <div className="user-name">{user?.name || 'User'}</div>
              <div className="user-sub">{isAdmin ? 'Admin' : 'Client'}</div>
            </div>
            <span style={{ display: 'flex', color: 'var(--text-muted)', transition: 'transform 0.15s ease', transform: menuOpen ? 'rotate(180deg)' : undefined }}>
              <I.chevronDown />
            </span>
          </button>

          {menuOpen && (
            <div className="card" style={{ position: 'absolute', right: 0, top: 'calc(100% + 8px)', zIndex: 60, minWidth: 210, padding: 6, boxShadow: 'var(--shadow-lg)' }}>
              <div style={{ padding: '8px 10px 10px', borderBottom: '1px solid var(--border)', marginBottom: 6 }}>
                <div className="user-name" style={{ fontSize: 13 }}>{user?.name || 'User'}</div>
                <div className="user-sub" style={{ fontSize: 11.5 }}>{user?.email}</div>
              </div>
              <button
                className="layer-row"
                style={{ width: '100%', gap: 9, padding: '8px 10px', borderRadius: 8, fontSize: 13 }}
                onClick={goSettings}
              >
                <span style={{ display: 'flex', color: 'var(--text-muted)', flexShrink: 0 }}><Icon name="settings" /></span>
                Settings
              </button>
              <button
                className="layer-row"
                style={{ width: '100%', gap: 9, padding: '8px 10px', borderRadius: 8, fontSize: 13, color: 'var(--danger)' }}
                onClick={signOut}
              >
                <span style={{ display: 'flex', flexShrink: 0 }}>{LOGOUT_ICON}</span>
                Sign out
              </button>
            </div>
          )}
        </div>

      </div>
    </div>
  )
}
